<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import SvgIcon from '@/components/SvgIcon.vue'
import { useNoticeCenter } from '@/composables/useNoticeCenter'
import { useToken } from '@/composables/useToken'
import notificationDotGif from '@/assets/imgs/header/user-popover/icon_notification_dot.gif'
import { apiAuthLogout } from '@/api/user'
import dialog from '@/utils/dialog'
import { ACCOUNT_MENU_ITEMS, type AccountMenuItem } from './accountMenu'
// 公用的二级菜单UI组件
const props = withDefaults(
  defineProps<{
    active?: boolean
    secondary?: boolean
    showLastItem?: boolean
  }>(),
  {
    active: false,
    secondary: false,
    showLastItem: false
  }
)

const emit = defineEmits<{
  select: []
}>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { totalUnreadCount } = useNoticeCenter()
const { clearToken } = useToken()
const isLoggingOut = ref(false)

const hasUnread = computed(() => Number(totalUnreadCount.value || 0) > 0)
const visibleMenuItems = computed(() => {
  return props.showLastItem ? ACCOUNT_MENU_ITEMS : ACCOUNT_MENU_ITEMS.slice(0, -1)
})

const isActive = (item: AccountMenuItem) => {
  return item.match.some((path) => route.path === path || route.path.startsWith(`${path}/`))
}

const handleLogout = async () => {
  if (isLoggingOut.value) return

  const confirmed = await dialog.confirm({
    title: t('登出账户'),
    content: t('确定退出登陆吗？')
  })
  if (!confirmed) return

  isLoggingOut.value = true
  try {
    await apiAuthLogout()
  } catch {
    // noop
  } finally {
    clearToken()
    location.href = '/'
    isLoggingOut.value = false
  }
}

const handleClick = (item: AccountMenuItem) => {
  if (item.action === 'logout') {
    handleLogout()
    return
  }
  if (props.active && isActive(item)) return
  emit('select')
  router.replace(item.router)
}
</script>

<template>
  <nav class="account-menu" :class="{ 'is-secondary': secondary }">
    <button
      v-for="item in visibleMenuItems"
      :key="item.key"
      type="button"
      class="menu-item"
      :class="{
        'is-divider': item.hasDivider,
        'is-top-divider': item.hasTopDivider,
        active: active && isActive(item)
      }"
      @click="handleClick(item)">
      <SvgIcon :name="item.iconName" size="20px" class="menu-icon" />
      <span class="menu-text">{{ t(item.label) }}</span>
      <img
        v-if="item.key === 'notification' && hasUnread"
        class="menu-dot-gif"
        :src="notificationDotGif"
        alt="" />
    </button>
  </nav>
</template>

<style scoped lang="scss">
.account-menu {
  width: 178px;
  min-height: 460px;
  padding: 10px;
  box-sizing: border-box;
  border-radius: 10px;
  background: #232221;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.is-secondary {
  flex: 0 0 178px;
  height: 460px;
  align-self: flex-start;
}

.menu-item {
  width: 100%;
  height: 38px;
  padding: 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  cursor: pointer;
  position: relative;
  text-align: left;
  transition: background 0.2s ease-in-out;

  &.is-divider {
    margin-bottom: 9px;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -6px;
      height: 0.6px;
      background: #4d4a43;
    }
  }
  &.is-top-divider {
    margin-top: 9px;
    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: -6px;
      height: 0.6px;
      background: #4d4a43;
    }
  }

  &.active {
    background: #3a352c;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
}

.menu-icon {
  flex-shrink: 0;
}

.menu-text {
  font-weight: 400;
  font-size: 14px;
  color: #eee;
  line-height: 1;
}

.menu-dot-gif {
  margin-left: auto;
  width: 14px;
  height: 14px;
  object-fit: contain;
}
</style>
